import React, { useState, useCallback } from 'react';

type VideoTutorial = {
  id: string;
  title: string;
  description: string;
  duration: string;
  category: 'getting-started' | 'editor' | 'git' | 'preview' | 'advanced';
  thumbnail?: string;
  videoUrl?: string;
  youtubeId?: string;
};

type VideoTutorialsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const tutorials: VideoTutorial[] = [
  {
    id: 'intro',
    title: 'Introduction to Atlas IDE',
    description: 'Learn the basics of Atlas IDE - file management, editor features, and live preview.',
    duration: '5:30',
    category: 'getting-started',
  },
  {
    id: 'file-management',
    title: 'File Management',
    description: 'Create, rename, move, and organize files and folders in your project.',
    duration: '4:15',
    category: 'getting-started',
  },
  {
    id: 'editor-basics',
    title: 'Editor Basics',
    description: 'Multi-cursor editing, code folding, search & replace, and keyboard shortcuts.',
    duration: '8:45',
    category: 'editor',
  },
  {
    id: 'intellisense',
    title: 'IntelliSense & Autocomplete',
    description: 'Code completions, hover documentation, signature help, and quick fixes.',
    duration: '6:20',
    category: 'editor',
  },
  {
    id: 'vim-mode',
    title: 'Vim Mode Tutorial',
    description: 'Enable and use Vim keybindings for efficient text editing.',
    duration: '10:00',
    category: 'editor',
  },
  {
    id: 'git-basics',
    title: 'Git Integration Basics',
    description: 'Stage, commit, push, and pull changes using the built-in Git support.',
    duration: '7:30',
    category: 'git',
  },
  {
    id: 'branching',
    title: 'Branch Management',
    description: 'Create, switch, merge, and delete branches in your repository.',
    duration: '5:45',
    category: 'git',
  },
  {
    id: 'diff-merge',
    title: 'Diff & Merge',
    description: 'View file differences, resolve conflicts, and compare branches.',
    duration: '6:00',
    category: 'git',
  },
  {
    id: 'live-preview',
    title: 'Live Preview',
    description: 'Real-time preview with hot reload, device emulation, and responsive testing.',
    duration: '5:15',
    category: 'preview',
  },
  {
    id: 'console-network',
    title: 'Console & Network Inspector',
    description: 'Debug with console logs and inspect network requests from your preview.',
    duration: '4:30',
    category: 'preview',
  },
  {
    id: 'keyboard-shortcuts',
    title: 'Keyboard Shortcuts Mastery',
    description: 'Master all keyboard shortcuts to boost your productivity.',
    duration: '8:00',
    category: 'advanced',
  },
  {
    id: 'customization',
    title: 'Customization & Themes',
    description: 'Customize your IDE with themes, settings, and preferences.',
    duration: '4:45',
    category: 'advanced',
  },
];

const categories = [
  { id: 'all', label: 'All Tutorials' },
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'editor', label: 'Editor' },
  { id: 'git', label: 'Git' },
  { id: 'preview', label: 'Preview' },
  { id: 'advanced', label: 'Advanced' },
];

export const VideoTutorialsModal = React.memo(function VideoTutorialsModal({
  isOpen,
  onClose,
}: VideoTutorialsModalProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTutorial, setSelectedTutorial] = useState<VideoTutorial | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTutorials = tutorials.filter((tutorial) => {
    const matchesCategory = selectedCategory === 'all' || tutorial.category === selectedCategory;
    const matchesSearch =
      !searchQuery ||
      tutorial.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tutorial.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleSelectTutorial = useCallback((tutorial: VideoTutorial) => {
    setSelectedTutorial(tutorial);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedTutorial(null);
  }, []);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'getting-started': return 'S';
      case 'editor': return 'E';
      case 'git': return 'G';
      case 'preview': return 'P';
      case 'advanced': return 'A';
      default: return '*';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'getting-started': return 'var(--success)';
      case 'editor': return 'var(--accent)';
      case 'git': return 'var(--warning)';
      case 'preview': return 'var(--info)';
      case 'advanced': return 'var(--danger)';
      default: return 'var(--muted)';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="video-tutorials-title"
        style={{ maxWidth: '800px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {selectedTutorial && (
              <button
                onClick={handleBack}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--muted)',
                  cursor: 'pointer',
                  padding: '0 8px 0 0',
                  fontSize: '14px',
                }}
                aria-label="Back to tutorial list"
              >
                &larr;
              </button>
            )}
            <span id="video-tutorials-title">
              {selectedTutorial ? selectedTutorial.title : 'Video Tutorials'}
            </span>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '0', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!selectedTutorial ? (
            <>
              {/* Search and filters */}
              <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
                <input
                  type="text"
                  placeholder="Search tutorials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    color: 'var(--text)',
                    fontSize: '13px',
                    marginBottom: '12px',
                  }}
                  aria-label="Search tutorials"
                />
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      className="pill-btn"
                      onClick={() => setSelectedCategory(cat.id)}
                      style={{
                        background: selectedCategory === cat.id ? 'var(--accent)' : 'var(--bg-secondary)',
                        color: selectedCategory === cat.id ? 'white' : 'var(--text)',
                        fontSize: '11px',
                        padding: '4px 10px',
                      }}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tutorial list */}
              <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
                  {filteredTutorials.map((tutorial) => (
                    <button
                      key={tutorial.id}
                      onClick={() => handleSelectTutorial(tutorial)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        padding: '0',
                        cursor: 'pointer',
                        textAlign: 'left',
                        overflow: 'hidden',
                        transition: 'border-color 0.2s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
                    >
                      {/* Thumbnail placeholder */}
                      <div
                        style={{
                          height: '120px',
                          background: `linear-gradient(135deg, ${getCategoryColor(tutorial.category)}22, ${getCategoryColor(tutorial.category)}44)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '48px',
                            color: getCategoryColor(tutorial.category),
                            opacity: 0.5,
                          }}
                        >
                          {getCategoryIcon(tutorial.category)}
                        </span>
                        {/* Play button overlay */}
                        <div
                          style={{
                            position: 'absolute',
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            background: 'rgba(0,0,0,0.6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '20px',
                          }}
                        >
                          &gt;
                        </div>
                        {/* Duration badge */}
                        <span
                          style={{
                            position: 'absolute',
                            bottom: '8px',
                            right: '8px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '11px',
                          }}
                        >
                          {tutorial.duration}
                        </span>
                      </div>
                      {/* Content */}
                      <div style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                          <span
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              background: getCategoryColor(tutorial.category),
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 'bold',
                            }}
                          >
                            {getCategoryIcon(tutorial.category)}
                          </span>
                          <span style={{ fontWeight: 'bold', fontSize: '13px', color: 'var(--text)' }}>
                            {tutorial.title}
                          </span>
                        </div>
                        <p style={{ fontSize: '11px', color: 'var(--muted)', margin: 0, lineHeight: 1.4 }}>
                          {tutorial.description}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>

                {filteredTutorials.length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '40px' }}>
                    No tutorials found matching your search.
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Tutorial detail view */
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Video player placeholder */}
              <div
                style={{
                  height: '300px',
                  background: `linear-gradient(135deg, ${getCategoryColor(selectedTutorial.category)}22, ${getCategoryColor(selectedTutorial.category)}44)`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    marginBottom: '16px',
                  }}
                >
                  <span style={{ fontSize: '32px', color: 'white', marginLeft: '4px' }}>&gt;</span>
                </div>
                <p style={{ color: 'var(--muted)', fontSize: '13px' }}>
                  Video tutorial coming soon
                </p>
              </div>

              {/* Tutorial info */}
              <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '4px',
                      background: getCategoryColor(selectedTutorial.category),
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: 'bold',
                    }}
                  >
                    {categories.find(c => c.id === selectedTutorial.category)?.label}
                  </span>
                  <span style={{ color: 'var(--muted)', fontSize: '12px' }}>
                    Duration: {selectedTutorial.duration}
                  </span>
                </div>

                <p style={{ fontSize: '14px', color: 'var(--text)', lineHeight: 1.6, marginBottom: '20px' }}>
                  {selectedTutorial.description}
                </p>

                <div
                  style={{
                    padding: '16px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text)' }}>
                    What you'll learn:
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--muted)', fontSize: '12px', lineHeight: 1.8 }}>
                    <li>Core concepts and best practices</li>
                    <li>Step-by-step walkthrough</li>
                    <li>Tips and shortcuts</li>
                    <li>Common pitfalls to avoid</li>
                  </ul>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="pill-btn"
                    style={{ background: 'var(--accent)', color: 'white', fontSize: '12px' }}
                    onClick={() => {
                      // Would open the video in a new tab or start playing
                      alert('Video tutorials will be available soon!');
                    }}
                  >
                    Watch Tutorial
                  </button>
                  <button
                    className="pill-btn"
                    style={{ fontSize: '12px' }}
                    onClick={handleBack}
                  >
                    Back to List
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <p style={{ fontSize: '11px', color: 'var(--muted)', margin: 0 }}>
            {filteredTutorials.length} tutorial{filteredTutorials.length !== 1 ? 's' : ''} available
          </p>
          <button className="pill-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

export default VideoTutorialsModal;
